import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, GraduationCap, Truck, Package,
  CreditCard, BarChart3, FileText, Headphones,
  UserCog, Settings, ChevronLeft, ChevronRight, Bell, Search,
  Moon, Sun, Menu, X, Building2, MapPin, LogOut
} from 'lucide-react';
import { adminAuth } from '../../services/adminAuth';

const NAV_ITEMS = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/reservations', icon: ClipboardList, label: 'Reservations' },
  { path: '/admin/schools', icon: GraduationCap, label: 'Schools' },
  { path: '/admin/branch-performance', icon: Building2, label: 'Branch Performance' },
  { path: '/admin/pickups', icon: Truck, label: 'Pickups' },
  { path: '/admin/deliveries', icon: MapPin, label: 'Deliveries' },
  { path: '/admin/inventory', icon: Package, label: 'Inventory' },
  { path: '/admin/payments', icon: CreditCard, label: 'Payments' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports & Analytics' },
  { path: '/admin/support', icon: Headphones, label: 'Support Tickets' },
  { path: '/admin/team', icon: UserCog, label: 'Team Management' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = adminAuth.getUser();

  const handleLogout = () => {
    adminAuth.logout();
    navigate('/admin/login', { replace: true });
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const bg = darkMode ? 'bg-[#0f1117]' : 'bg-gray-50';
  const sidebarBg = darkMode ? 'bg-[#161822]' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-[#1e2030]' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-[#161822]' : 'bg-white';
  const hoverBg = darkMode ? 'hover:bg-[#1e2030]' : 'hover:bg-gray-100';
  const activeBg = darkMode ? 'bg-[#1e2030]' : 'bg-blue-50';
  const activeText = darkMode ? 'text-blue-400' : 'text-blue-600';

  return (
    <div className={`min-h-screen ${bg} ${textPrimary} flex`} data-testid="admin-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`${sidebarBg} border-r ${borderColor} fixed lg:sticky top-0 h-screen z-50 transition-all duration-200 flex flex-col
        ${collapsed ? 'w-[68px]' : 'w-[240px]'}
        ${mobileOpen ? 'left-0' : '-left-[240px] lg:left-0'}
      `} data-testid="admin-sidebar">
        {/* Logo */}
        <div className={`h-16 flex items-center px-4 border-b ${borderColor} shrink-0`}>
          {!collapsed && (
            <span className="text-lg font-bold tracking-tight whitespace-nowrap" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <span className="text-blue-400">Student</span> Store It
            </span>
          )}
          {collapsed && <span className="text-blue-400 font-bold text-xl mx-auto">S</span>}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${active ? `${activeBg} ${activeText}` : `${textSecondary} ${hoverBg}`}
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <Icon className="w-[18px] h-[18px] shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {active && !collapsed && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400" />}
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <div className={`hidden lg:flex items-center justify-center border-t ${borderColor} h-12 shrink-0`}>
          <button onClick={() => setCollapsed(!collapsed)} className={`${textSecondary} ${hoverBg} p-1.5 rounded-md`}>
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className={`h-16 ${cardBg} border-b ${borderColor} flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30`} data-testid="admin-topbar">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-md" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className={`relative hidden md:block`}>
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textSecondary}`} />
              <input
                placeholder="Search reservations, schools..."
                className={`${darkMode ? 'bg-[#1e2030] text-white placeholder:text-gray-500' : 'bg-gray-100 text-gray-900 placeholder:text-gray-400'} pl-10 pr-4 py-2 rounded-lg text-sm w-[300px] border-0 outline-none focus:ring-2 focus:ring-blue-500/30`}
                data-testid="global-search"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg ${hoverBg} ${textSecondary}`}
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button className={`p-2 rounded-lg ${hoverBg} ${textSecondary} relative`} data-testid="notifications-btn">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className={`hidden sm:flex items-center gap-2 ml-2 pl-3 border-l ${borderColor}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                {user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` : 'AD'}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium leading-none">{user ? `${user.first_name} ${user.last_name}` : 'Admin'}</p>
                <p className={`text-xs ${textSecondary} mt-0.5 capitalize`}>{user?.role?.replace('_', ' ') || 'Admin'}</p>
              </div>
              <button onClick={handleLogout} className={`ml-2 p-2 rounded-lg ${hoverBg} ${textSecondary} hover:text-red-400`} title="Sign out" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet context={{ darkMode, cardBg, textPrimary, textSecondary, borderColor, hoverBg }} />
        </main>
      </div>
    </div>
  );
}
